<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHelpStore } from '@/stores/help'
import { apiClient } from '@/api/client'
import { useToast } from 'primevue/usetoast'

interface GuideSezioneDTO {
  id: number
  slug: string
  titolo: string
  contenutoHtml: string | null
  videoUrl: string | null
  paginaSlug: string | null
}

const { t } = useI18n()
const helpStore = useHelpStore()
const toast = useToast()
const sezione = ref<GuideSezioneDTO | null>(null)
const loading = ref(false)
const notFound = ref(false)

watch(() => helpStore.currentSlug, async (slug) => {
  if (!slug) return
  loading.value = true
  notFound.value = false
  sezione.value = null
  try {
    const res = await apiClient.get<GuideSezioneDTO>('/api/v2/guide', { params: { pagina: slug } })
    sezione.value = res.data
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number } }
    if (axiosErr?.response?.status === 404) {
      notFound.value = true
    } else {
      toast.add({ severity: 'error', summary: t('common.errore', 'Errore'), detail: t('guida.drawer.caricaErrore', 'Impossibile caricare la guida.'), life: 3000 })
    }
  } finally {
    loading.value = false
  }
}, { immediate: false })

function extractYoutubeId(url: string): string | null {
  const patterns = [
    /youtube\.com\/watch\?(?:.*&)?v=([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match?.[1]) return match[1]
  }
  return null
}

function getYoutubeThumbnail(url: string): string | null {
  const id = extractYoutubeId(url)
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null
}

function isYoutube(url: string): boolean {
  return !!extractYoutubeId(url)
}

/**
 * True per un video che ospitiamo noi (file servito dal nostro web server), da riprodurre inline.
 * I servizi esterni (YouTube/Vimeo) restano un link con anteprima: non si incorpora un player
 * di terzi dentro il portale.
 */
function isVideoFile(url: string): boolean {
  return /\.(webm|mp4|ogv|ogg|mov)(\?.*)?$/i.test(url)
}


</script>

<template>
  <Drawer
    v-model:visible="helpStore.isOpen"
    position="right"
    :style="{ width: '480px' }"
    :header="t('guida.drawer.titolo', 'Guida')"
    @hide="helpStore.close()"
  >
    <!-- Loading -->
    <div
      v-if="loading"
      class="flex justify-center items-center h-32"
    >
      <ProgressSpinner style="width: 40px; height: 40px" />
    </div>

    <!-- Nessuna guida -->
    <div
      v-else-if="notFound"
      class="flex flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <i class="pi pi-info-circle text-4xl text-muted-color" />
      <p class="text-muted-color m-0">
        {{ t('guida.drawer.nessuna', 'Nessuna guida disponibile per questa pagina.') }}
      </p>
    </div>

    <!-- Contenuto guida -->
    <div
      v-else-if="sezione"
      class="flex flex-col gap-4"
    >
      <h2 class="text-xl font-bold m-0 text-color">
        {{ sezione.titolo }}
      </h2>

      <!--
        v-html è accettabile qui: il contenuto HTML proviene esclusivamente
        dall'API backend interna (/api/v2/guide) e NON da input utente diretto.
        Il backend è responsabile della sanitizzazione prima della persistenza.
      -->
      <div
        v-if="sezione.contenutoHtml"
        class="help-content prose"
        v-html="sezione.contenutoHtml"
      />

      <!-- Video ospitato da noi (.webm/.mp4/.ogv): riproduttore inline, senza uscire dal drawer.
           I video delle guide stanno FUORI dal repo (cartella servita dal web server), quindi qui
           arriva un URL, non un file del progetto. -->
      <video
        v-if="sezione.videoUrl && isVideoFile(sezione.videoUrl)"
        :src="sezione.videoUrl"
        controls
        preload="metadata"
        playsinline
        class="w-full rounded-lg border border-surface-200"
        style="max-height: 60vh; background: #000"
      />

      <!-- Video preview: thumbnail cliccabile → apre YouTube/Vimeo in nuova tab -->
      <a
        v-else-if="sezione.videoUrl"
        :href="sezione.videoUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="video-thumb-link block"
      >
        <!-- Thumbnail YouTube -->
        <div
          v-if="isYoutube(sezione.videoUrl)"
          class="relative rounded-lg overflow-hidden"
          style="aspect-ratio: 16/9"
        >
          <img
            :src="getYoutubeThumbnail(sezione.videoUrl)!"
            class="w-full h-full object-cover"
            :alt="t('guida.drawer.anteprimaVideo', 'Anteprima video')"
          >
          <!-- Overlay play button -->
          <div class="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/40 transition-colors">
            <div class="bg-red-600 rounded-full w-14 h-14 flex items-center justify-center shadow-lg">
              <i class="pi pi-play text-white text-xl ml-1" />
            </div>
          </div>
          <div class="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
            YouTube
          </div>
        </div>

        <!-- Vimeo / link generico senza thumbnail -->
        <div
          v-else
          class="flex items-center gap-3 p-3 rounded-lg border border-surface-200 hover:border-primary transition-colors"
        >
          <i class="pi pi-play-circle text-3xl text-primary" />
          <div>
            <div class="font-medium text-sm text-color">{{ t('guida.drawer.guardaVideo', 'Guarda il video') }}</div>
            <div class="text-xs text-muted-color truncate max-w-xs">{{ sezione.videoUrl }}</div>
          </div>
          <i class="pi pi-external-link text-muted-color ml-auto" />
        </div>
      </a>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <Button
          :label="t('common.chiudi', 'Chiudi')"
          severity="secondary"
          outlined
          size="small"
          @click="helpStore.close()"
        />
      </div>
    </template>
  </Drawer>
</template>

<style scoped>
.help-content :deep(h1),
.help-content :deep(h2),
.help-content :deep(h3) {
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  color: var(--p-text-color);
}
.help-content :deep(p) {
  margin-bottom: 0.75rem;
  color: var(--p-text-color);
  line-height: 1.6;
}
.help-content :deep(ul),
.help-content :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
}
.help-content :deep(li) {
  margin-bottom: 0.25rem;
  color: var(--p-text-color);
}
.help-content :deep(code) {
  background: var(--p-surface-100);
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
  font-size: 0.875rem;
}
.video-thumb-link {
  text-decoration: none;
  cursor: pointer;
}
.video-thumb-link:hover img {
  transform: scale(1.02);
  transition: transform 0.2s ease;
}
</style>
